// Reads AgentLedger rows (Table Storage) and groups them into conversations.
// The physical timestamp column is `eventTimestamp` (renamed the logical
// `timestamp` to dodge the reserved system Timestamp). PK = lowercase agentName.
// Pure map/group split from the single I/O call. The TableClient is
// INJECTED — no env/credential coupling lives here (the caller
// builds the client at the entry point).
import type { TableClient } from "@azure/data-tables";
import type { LedgerConversation, LedgerRow } from "./types.js";
import { resolveIdentity, type TeamMemberLite } from "./ledger-identity.js";

export interface LedgerFilters { workers: string[]; source: string; from: string; to: string; foundryConversationId?: string }

// Azure Table Storage returns all columns as primitive scalars (string | number | boolean | null).
// We coerce to string with explicit type guards covering all expected value types.
const str = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return ""; // unreachable for table data (always a primitive at runtime)
};
const req = (v: unknown, fallback = ""): string => str(v) ?? fallback;

function parseArtifacts(v: unknown): { path: string; mime?: string }[] | undefined {
  if (typeof v !== "string" || v === "") return undefined;
  try {
    const a: unknown = JSON.parse(v);
    if (!Array.isArray(a)) return undefined;
    const out = (a as unknown[])
      .filter((x): x is { path: string; mime?: unknown } => !!x && typeof (x as Record<string, unknown>).path === "string")
      .map((x) => (typeof x.mime === "string" ? { path: x.path, mime: x.mime } : { path: x.path }));
    return out.length ? out : undefined;
  } catch {
    return undefined;
  }
}

export function mapEntity(e: Record<string, unknown>): LedgerRow {
  return {
    eventId: req(e.eventId ?? e.rowKey),
    agentName: req(e.agentName ?? e.partitionKey),
    source: req(e.source),
    channel: str(e.channel),
    userRef: str(e.userRef),
    foundryConversationId: str(e.foundryConversationId),
    responseId: str(e.responseId),
    model: str(e.model),
    outcome: req(e.outcome, "ok"),
    eventTimestamp: req(e.eventTimestamp),
    rowKey: req(e.rowKey),
    latencyMs: e.latencyMs == null ? undefined : Number(e.latencyMs),
    errorCode: str(e.errorCode),
    inputSnippet: str(e.inputSnippet),
    replySnippet: str(e.replySnippet),
    initiatingAgent: str(e.initiatingAgent),
    delegationId: str(e.delegationId),
    parentEventId: str(e.parentEventId),
    depth: e.depth == null ? undefined : Number(e.depth),
    artifacts: parseArtifacts(e.artifacts),
  };
}

// "ok" ranks below any non-ok outcome so the worst surfaces.
const rank = (o: string): number => (o === "ok" ? 0 : 1);

// A detached delegation emits a started in_progress row + a final ok/error row
// sharing one eventId. Collapse same-eventId rows, preferring the settled
// (non-in_progress) one, so the started/finished pair counts as one round.
function dedupPairs(rows: LedgerRow[]): LedgerRow[] {
  const byEvent = new Map<string, LedgerRow>();
  for (const r of rows) {
    const prev = byEvent.get(r.eventId);
    if (!prev || (prev.outcome === "in_progress" && r.outcome !== "in_progress")) byEvent.set(r.eventId, r);
  }
  return [...byEvent.values()];
}
function mergeArtifacts(rows: LedgerRow[]): { path: string; mime?: string }[] | undefined {
  const byPath = new Map<string, { path: string; mime?: string }>();
  for (const r of rows) for (const a of r.artifacts ?? []) if (!byPath.has(a.path)) byPath.set(a.path, a);
  return byPath.size ? [...byPath.values()] : undefined;
}

export function groupIntoConversations(rows: LedgerRow[], members: TeamMemberLite[] = []): LedgerConversation[] {
  const groups = new Map<string, LedgerRow[]>();
  for (const r of rows) {
    const key = r.source === "a2a"
      ? `a2a:${r.delegationId ?? r.eventId}`
      : (r.foundryConversationId ?? `__single__:${r.responseId ?? r.eventId}`);
    const arr = groups.get(key);
    if (arr) arr.push(r);
    else groups.set(key, [r]);
  }
  const convs: LedgerConversation[] = [];
  for (const grp of groups.values()) {
    const isA2a = grp[0].source === "a2a";
    const deduped = isA2a ? dedupPairs(grp) : grp;
    const sorted = [...deduped].sort((a, b) => b.eventTimestamp.localeCompare(a.eventTimestamp)); // newest first
    const newest = sorted[0];
    const worst = sorted.reduce((acc, r) => (rank(r.outcome) > rank(acc) ? r.outcome : acc), "ok");
    const conv: LedgerConversation = {
      conversationId: newest.foundryConversationId,
      worker: newest.agentName,
      source: newest.source,
      user: resolveIdentity(newest.source, newest.userRef, members),
      startedAt: sorted[sorted.length - 1].eventTimestamp,
      lastAt: newest.eventTimestamp,
      rounds: sorted.length,
      outcome: worst,
      enriched: false,
      detail: sorted.map((r) => ({
        responseId: r.responseId, at: r.eventTimestamp, outcome: r.outcome,
        latencyMs: r.latencyMs, model: r.model, errorCode: r.errorCode, depth: r.depth,
        inputSnippet: r.inputSnippet, replySnippet: r.replySnippet,
      })),
    };
    if (isA2a) {
      conv.initiatingAgent = newest.initiatingAgent ?? newest.userRef;
      conv.target = newest.agentName;
      conv.delegationId = newest.delegationId;
      conv.depth = newest.depth;
      conv.artifacts = mergeArtifacts(sorted);
    }
    convs.push(conv);
  }
  return convs.sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}

// OData string literals escape a single quote by doubling it.
const esc = (s: string) => s.replace(/'/g, "''");

// eventTimestamp is Edm.String; ISO-8601 UTC sorts lexically, so ge/le bound the range.
function odataFilter(worker: string | undefined, f: LedgerFilters): string {
  const parts = [`eventTimestamp ge '${esc(f.from)}' and eventTimestamp le '${esc(f.to)}'`];
  if (worker) parts.unshift(`PartitionKey eq '${esc(worker)}'`);
  if (f.source !== "all") parts.push(`source eq '${esc(f.source)}'`);
  if (f.foundryConversationId) parts.push(`foundryConversationId eq '${esc(f.foundryConversationId)}'`);
  return parts.join(" and ");
}

/**
 * Match an agentName against a comma-separated list of glob patterns
 * (`*` → any sequence). Full-match, case-insensitive.
 * Returns true if any pattern matches.
 */
export function matchesAnyPattern(agentName: string, patterns: string[]): boolean {
  if (!patterns.length) return false;
  const lower = agentName.toLowerCase();
  return patterns.some((p) => {
    const re = new RegExp(`^${p.toLowerCase().replace(/[.+^${}()|[\]\\?]/g, "\\$&").replace(/\*/g, ".*")}$`);
    return re.test(lower);
  });
}

/**
 * Apply M8T_LEDGER_EXCLUDE_PATTERNS filter to a set of rows.
 * `patterns` is a comma-separated glob list (e.g. "*-smoke,a2a-f*").
 * Off when empty/unset — identical behavior.
 */
export function applyExcludePatterns(
  rows: LedgerRow[],
  patterns: string[],
): { rows: LedgerRow[]; excludedCount: number } {
  if (!patterns.length) return { rows, excludedCount: 0 };
  const kept: LedgerRow[] = [];
  let excludedCount = 0;
  for (const r of rows) {
    if (matchesAnyPattern(r.agentName, patterns)) excludedCount++;
    else kept.push(r);
  }
  return { rows: kept, excludedCount };
}

export async function fetchLedgerRows(filters: LedgerFilters, client: TableClient): Promise<LedgerRow[]> {
  const workers = [...new Set(filters.workers.map((w) => w.toLowerCase()))];
  const queries = workers.length > 0
    ? workers.map((w) => odataFilter(w, filters))
    : [odataFilter(undefined, filters)];
  const out: LedgerRow[] = [];
  for (const filter of queries) {
    for await (const e of client.listEntities({ queryOptions: { filter } })) {
      out.push(mapEntity(e));
    }
  }
  return out;
}
